import { useState, useRef, useEffect } from 'react'
import { Plus, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'

// How long a click on the open pill waits before closing it, so a double
// click (rename) can cancel it. Generous enough for slower double clicks.
const CLOSE_DELAY_MS = 300

interface ViewSwitcherProps {
  /** "classic" shows the All button + square tabs. "compact" shows pill-style saved views only (no All). */
  variant?: 'classic' | 'compact'
  views: TableView[]
  activeViewUuid: string | null
  onSelectView: (view: TableView | null) => void
  onSaveView: (name: string, config: TableViewConfig) => void
  onDeleteView: (uuid: string) => void
  onRenameView: (uuid: string, name: string) => void
  onSetOrgDefault?: (config: TableViewConfig) => void
  currentConfig: TableViewConfig
  /** The open view has changes not saved to it yet (or it can't be saved). */
  activeViewModified?: boolean
  /**
   * The open view is one others see (org default, or shared) and has changes
   * waiting: shows "Save changes", which confirms before saving for everyone.
   */
  activeViewNeedsSave?: boolean
  onSaveActiveView?: () => void
  /**
   * Whether the user may rename or delete a view (their own, or an org
   * default they manage). Others' shared views get neither. Defaults to yes.
   */
  canEditView?: (view: TableView) => boolean
  canManageDefaults?: boolean
}

export function ViewSwitcher({
  variant = 'classic',
  views,
  activeViewUuid,
  onSelectView,
  onSaveView,
  onDeleteView,
  onRenameView,
  onSetOrgDefault,
  currentConfig,
  activeViewModified = false,
  activeViewNeedsSave = false,
  onSaveActiveView,
  canEditView = () => true,
  canManageDefaults,
}: ViewSwitcherProps) {
  const { t } = useTranslation()
  const [isNaming, setIsNaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [renamingUuid, setRenamingUuid] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  // Changes that reach everyone (saving a shared view, setting the org
  // default) are confirmed first, so nobody does it by accident.
  const [confirmingSave, setConfirmingSave] = useState(false)
  const [defaultCandidate, setDefaultCandidate] = useState<TableView | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  // A click on the open pill closes the view, but a double click renames it:
  // hold the close briefly so the double click can cancel it.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    },
    [],
  )

  // Focus input when naming or renaming
  useEffect(() => {
    if (isNaming && inputRef.current) inputRef.current.focus()
  }, [isNaming])

  useEffect(() => {
    if (renamingUuid && renameRef.current) renameRef.current.focus()
  }, [renamingUuid])

  // Set once the name box is saved or cancelled, so the blur that follows
  // (the box closing) does not act on it a second time.
  const namingSettledRef = useRef(false)

  function startNaming() {
    namingSettledRef.current = false
    setIsNaming(true)
  }

  function stopNaming() {
    namingSettledRef.current = true
    setNewName('')
    setIsNaming(false)
  }

  function handleSave() {
    const name = newName.trim()
    if (!name || namingSettledRef.current) return
    onSaveView(name, currentConfig)
    stopNaming()
  }

  function handleRename(uuid: string) {
    const name = renameValue.trim()
    const view = views.find((v) => v.uuid === uuid)
    setRenamingUuid(null)
    setRenameValue('')
    // Empty name asks to delete (via a non-dismissible confirm), so clicking
    // away can't nuke a view by accident. An unchanged name just closes.
    if (!name) {
      setDeletingUuid(uuid)
    } else if (view && name !== view.name) {
      onRenameView(uuid, name)
    }
  }

  // Saving is the page's job (useSavedListView): a private view saves itself
  // after a change; a view others see waits for "Save changes" below.

  const isCompact = variant === 'compact'
  const deletingView = deletingUuid ? views.find((v) => v.uuid === deletingUuid) : null
  const activeView = views.find((v) => v.uuid === activeViewUuid) ?? null
  const showSaveChanges = activeViewNeedsSave && !!activeView && !!onSaveActiveView

  return (
    <div className="cv-data-table flex min-w-0 flex-nowrap items-center gap-1">
      {/* "All" tab - only in classic mode */}
      {!isCompact && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onSelectView(null)}
          className={`cv-data-table-compact h-auto cursor-pointer shrink-0 rounded-[4px] border-[0.5px] border-solid px-3 py-1 font-normal uppercase tracking-[0.08em] transition-all duration-150 ${
            activeViewUuid === null
              ? 'bg-[var(--green-bg)] text-[var(--green)] border-[var(--green-border,var(--green))] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
              : 'text-[var(--ink-3)] border-[var(--rule)] hover:bg-[var(--bg-subtle)]'
          }`}
        >
          {t('common.all', { defaultValue: 'All' })}
        </Button>
      )}
      {/* Saved views */}
      {views.map((view) => {
        const isActive = activeViewUuid === view.uuid
        const unsaved = isActive && activeViewModified
        const editable = canEditView(view)
        const startRename = () => {
          if (!editable) return
          setRenamingUuid(view.uuid)
          setRenameValue(view.name)
        }

        return (
          <div key={view.uuid} className="relative shrink-0 group/tab">
            {renamingUuid === view.uuid ? (
              <Input
                ref={renameRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => handleRename(view.uuid)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename(view.uuid)
                  if (e.key === 'Escape') {
                    setRenamingUuid(null)
                    setRenameValue('')
                  }
                }}
                className="cv-data-table-compact w-[120px] rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 py-1 uppercase tracking-[0.08em] text-[var(--ink)] outline-none"
              />
            ) : isCompact ? (
              /* Compact: sentence-case pill with × dismiss */
              (<div
                className={`cv-data-table-body inline-flex max-w-[160px] shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 transition-all duration-[120ms]${
                  isActive
                    ? 'border-[0.5px] border-transparent bg-[var(--green)] text-white'
                    : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]'
                }`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => {
                  if (!isActive) {
                    onSelectView(view)
                    return
                  }
                  // No "All" pill in the compact layout: the open pill closes.
                  if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                  closeTimerRef.current = setTimeout(() => onSelectView(null), CLOSE_DELAY_MS)
                }}
                onDoubleClick={() => {
                  if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                  closeTimerRef.current = null
                  startRename()
                }}
                title={view.name}
              >
                <span className="truncate">{view.name}</span>
                {editable && (
                  <span
                    className="shrink-0 text-lg leading-none opacity-40 transition-opacity duration-100 hover:opacity-90"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeletingUuid(view.uuid)
                    }}
                  >
                    &times;
                  </span>
                )}
              </div>)
            ) : (
              /* Classic: square tab with uppercase */
              (<Button
                variant="ghost"
                size="xs"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelectView(view)}
                onDoubleClick={startRename}
                className={`cv-data-table-compact h-auto max-w-[160px] cursor-pointer items-center gap-0 rounded-[4px] border-[0.5px] border-solid px-3 py-1 font-normal uppercase tracking-[0.08em] transition-all duration-150 ${
                  isActive
                    ? 'bg-[var(--green-bg)] text-[var(--green)] border-[var(--green-border,var(--green))] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
                    : 'text-[var(--ink-3)] border-[var(--rule)] hover:bg-[var(--bg-subtle)]'
                }`}
                title={view.name}
              >
                {view.is_org_default ? (
                  <Star className="mr-1 size-2.5 shrink-0 fill-current" aria-label="Org default" />
                ) : canManageDefaults && onSetOrgDefault ? (
                  <span
                    role="button"
                    aria-label="Set as org default"
                    title="Set as org default"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDefaultCandidate(view)
                    }}
                    className="mr-1 flex shrink-0 cursor-pointer opacity-0 transition-opacity duration-100 group-hover/tab:opacity-100"
                  >
                    <Star className="size-2.5" />
                  </span>
                ) : null}
                <span className="truncate">{view.name}</span>
                {unsaved && (
                  <span
                    className="ml-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]"
                    title="Unsaved changes"
                  />
                )}
              </Button>)
            )}
          </div>
        );
      })}
      {showSaveChanges && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setConfirmingSave(true)}
          className="h-auto shrink-0 cursor-pointer rounded-[4px] px-2 py-1 font-normal text-[var(--green)] hover:bg-[var(--green-bg)]"
        >
          {t('viewSwitcher.saveChanges', 'Save changes')}
        </Button>
      )}
      {/* New view input or button */}
      {isNaming ? (
        <Input
          ref={inputRef}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          // Clicking away keeps a typed name as a saved view (there is no
          // separate Save button); an empty box just closes. Escape cancels.
          onBlur={() => {
            if (namingSettledRef.current) return
            if (newName.trim()) handleSave()
            else stopNaming()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            }
            if (e.key === 'Escape') stopNaming()
          }}
          placeholder="View name, then enter"
          className={`outline-none ${
            isCompact
              ? 'cv-data-table-body w-[160px] rounded-full border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3.5 py-1 text-[var(--ink)]'
              : 'cv-data-table-compact w-[160px] rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 py-1 uppercase tracking-[0.08em] text-[var(--ink)]'
          }`}
        />
      ) : (
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={startNaming}
          className="h-6 w-6 flex items-center justify-center rounded-full transition-all duration-150 cursor-pointer shrink-0 hover:bg-[var(--bg-subtle)] text-[var(--ink-3)] border-[0.5px] border-solid border-[var(--rule)]"
          title={t('common.saveView', { defaultValue: 'Save current view' })}
        >
          <Plus className="size-3" />
        </Button>
      )}
      {deletingView && (
        <ConfirmDeleteDialog
          open={!!deletingUuid}
          onOpenChange={(o) => {
            if (!o) setDeletingUuid(null)
          }}
          dismissible={false}
          title={t('viewSwitcher.deleteTitle', {
            defaultValue: 'Delete "{{name}}"?',
            name: deletingView.name,
          })}
          description={t('viewSwitcher.deleteDescription', {
            defaultValue: "This removes the saved view and can't be undone.",
          })}
          onConfirm={() => {
            onDeleteView(deletingView.uuid)
            setDeletingUuid(null)
          }}
        />
      )}
      {showSaveChanges && activeView && onSaveActiveView && (
        <ConfirmationDialog
          open={confirmingSave}
          onOpenChange={setConfirmingSave}
          title={t('viewSwitcher.saveSharedTitle', {
            defaultValue: 'Save changes to "{{name}}"?',
            name: activeView.name,
          })}
          description={
            activeView.is_org_default
              ? t(
                  'viewSwitcher.saveOrgDefaultDescription',
                  'This changes it for everyone in your organisation.',
                )
              : t(
                  'viewSwitcher.saveSharedDescription',
                  'This changes it for everyone it is shared with.',
                )
          }
          confirmLabel={t('viewSwitcher.saveForEveryone', 'Save for everyone')}
          onConfirm={() => {
            onSaveActiveView()
            setConfirmingSave(false)
          }}
        />
      )}
      {defaultCandidate && onSetOrgDefault && (
        <ConfirmationDialog
          open
          onOpenChange={(o) => {
            if (!o) setDefaultCandidate(null)
          }}
          title={t('viewSwitcher.setDefaultTitle', {
            defaultValue: 'Make "{{name}}" the default view?',
            name: defaultCandidate.name,
          })}
          description={t(
            'viewSwitcher.setDefaultDescription',
            'This becomes the default view for everyone in your organisation.',
          )}
          confirmLabel={t('viewSwitcher.setDefaultConfirm', 'Make default')}
          onConfirm={() => {
            onSetOrgDefault(defaultCandidate.config)
            setDefaultCandidate(null)
          }}
        />
      )}
    </div>
  );
}
